import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { QueryKey, queryOptions } from '@tanstack/react-query'
import { groupBy, map, pipe, sort } from 'remeda'
import { z } from 'zod'

import { BackedByQueryResult, SavingsBackedByData } from '@/config/chain/types'
import { spark2ApiUrl } from '@/config/consts'
import { normalizedNumberSchema } from '@/domain/common/validation'

export function skySavingsBackedByOptions(_args: { chainId: number; wagmiConfig: unknown }): BackedByQueryResult {
  return {
    queryOptions: queryOptions({
      queryKey: skySavingsBackedByKey(),
      queryFn: () => skySavingsBackedByFunction(),
    }),
    groupUsdsDetails: false,
  }
}

export function skySavingsBackedByKey(): QueryKey {
  return ['susds-backed-by']
}

export async function skySavingsBackedByFunction(): Promise<SavingsBackedByData> {
  const res = await fetch(`${spark2ApiUrl}/backed/?group=category`)
  if (!res.ok) {
    throw new Error('Failed to fetch backed by data')
  }
  return responseSchema.parse(await res.json()).data
}

export const responseSchema = z.object({
  data: z
    .array(
      z.object({
        category: z.string(),
        backed: normalizedNumberSchema,
      }),
    )
    .transform((data) =>
      pipe(
        data,
        mergeUnknownCategoriesWithOtherCategory,
        sort((a, b) => b.backed.comparedTo(a.backed)),
        map((item) => ({
          category: item.category as SavingsBackedByData[number]['category'],
          amount: item.backed,
        })),
      ),
    ),
})

const knownCategories = [
  'aaa_corporate_debt',
  'basis_trade',
  'onchain_crypto_lending',
  'otc_crypto_lending',
  'short_duration_treasury_bills',
  'stablecoins',
  'private_credit',
  'basis_trade_cme',
  'other',
] as const

function mergeUnknownCategoriesWithOtherCategory(
  data: {
    category: string
    backed: NormalizedNumber
  }[],
): {
  category: string
  backed: NormalizedNumber
}[] {
  return pipe(
    data,
    groupBy((item) => (knownCategories.includes(item.category) ? item.category : 'other')),
    (grouped) =>
      Object.entries(grouped).map(([category, items]) => {
        const backed = items.reduce((sum, item) => sum.plus(item.backed), NormalizedNumber.ZERO)
        return {
          category,
          backed,
        }
      }),
  )
}
